import type { BloomMenuTheme } from '@iheartjane/data-access';
import type { FeatureFlag } from '@iheartjane/feature-flags';
import type { CompareProduct, WizardStepKey } from '@iheartjane/menu';
import { compareProductsTrackingSummary } from '@iheartjane/menu';
import { EventNames } from '@iheartjane/tracking';
import type { Nullish, SpecialType } from '@iheartjane/types';

import {
  buildCustomComponentProperties,
  buildCustomerRatingProperties,
} from '@jane/bloom/services/tracking/buildProperties';
import type {
  AuthSource,
  StoreType,
  WizardOpenSource,
} from '@jane/bloom/services/tracking/Events';
import {
  addToCartPayload,
  viewItemPayload,
} from '@jane/bloom/services/tracking/googleAnalytics/ecommerce';
import type { Cart } from '@jane/bloom/types/schemas/bloom/checkout';
import type { FulfillmentMode } from '@jane/bloom/types/schemas/bloom/fulfillmentMode';
import type { ReservationMode } from '@jane/bloom/types/schemas/marketplace/reservationMode';
import type { StoreSpecial } from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import type { AutocompleteProduct } from '@jane/bloom/viewModels/autocompleteProduct';
import { getPriceIncludingDiscounts } from '@jane/bloom/viewModels/getPrice';
import type { ProductViewModel } from '@jane/bloom/viewModels/productDetail';
import type {
  ProductSearchCriteria,
  ProductSearchResultViewModel,
} from '@jane/bloom/viewModels/productSearch';

import { track } from './mixpanel';
import { buildSearchedMenuProductsProperties } from './track.utils';

interface TrackAddedProductToCartArgs {
  cardLocation?: 'wizard';
  cart: Nullish<Cart>;
  fulfillmentMode: FulfillmentMode;
  index?: number;
  product: ProductSearchResultViewModel | ProductViewModel;
  quantity: number;
  reservationMode?: ReservationMode;
  selectedWeight: Nullish<string>;
}

interface TrackProductListingTapArgs {
  cardLocation?: 'wizard';
  fulfillmentMode: FulfillmentMode;
  index?: number;
  product: ProductSearchResultViewModel | AutocompleteProduct;
  reservationMode?: ReservationMode;
  storeId?: Nullish<number | string>;
}

interface TrackCustomerRatingArgs {
  fulfillmentMode: FulfillmentMode;
  rating: number;
  reservationId: number;
  reservationMode: ReservationMode;
}

interface TrackMenuTapArgs {
  defaultOption: string;
  menuOptionSelected: string;
  reservationMode?: ReservationMode;
}

interface TrackRegisteredArgs {
  googleSsoEnabled: boolean;
  method: 'apple' | 'email' | 'google';
  source: AuthSource;
}

type TrackLoggedInArgs = TrackRegisteredArgs;

interface TrackHeaderTapArgs {
  reservationMode?: ReservationMode;
  tappable:
    'logo' | 'bag icon' | 'search icon' | 'profile icon' | 'hamburger menu';
}

interface TrackSearchedMenuProductsArgs {
  hits: number;
  searchCriteria: ProductSearchCriteria;
  theme: BloomMenuTheme;
}

interface TrackStoreSpecialTapArgs {
  source: 'account landing page' | 'bag' | 'checkout' | 'specials row';
  specialId: number;
  specialRankOnList: number;
  specialType: SpecialType;
}

const getStoreType = (fulfillmentMode: FulfillmentMode): StoreType => {
  switch (fulfillmentMode) {
    case 'both':
      return 'all';
    case 'rec':
      return 'recreational';
    case 'med':
      return 'medical';
  }
};

export const trackAddedProductToCart = (
  {
    cardLocation,
    cart,
    fulfillmentMode,
    index,
    product,
    quantity,
    reservationMode,
    selectedWeight,
  }: TrackAddedProductToCartArgs,
  {
    'ob-gtag-events': gtagEventsEnabled = false,
  }: Partial<Record<FeatureFlag, boolean>> = {}
) => {
  const price = getPriceIncludingDiscounts(product, selectedWeight ?? 'each');

  const eventDetails = {
    brand: product.brand,
    category: product.category,
    kind: product.kind,
    name: product.name,
    price_id: selectedWeight || 'each',
  };
  const ecommerce = addToCartPayload({
    price,
    product: eventDetails,
    productId: product.product_id,
    quantity,
    storeId: cart?.storeId,
  });
  window.dataLayer?.push({
    ecommerce,
    event: 'add_to_cart',
    product: eventDetails,
    productId: product.product_id,
    products: ecommerce.items,
  });
  if (gtagEventsEnabled) {
    window.gtag?.('event', 'add_to_cart', ecommerce);
  }

  return track({
    brandId: Number(product?.product_brand_id) || null,
    cardLocation,
    cartId: cart?.id,
    event: EventNames.AddedProductToCart,
    hasMultipleWeights: product?.available_weights.length > 1,
    index,
    pricePerUnit: price,
    priceTotal: price ? price * quantity : null,
    productBrand: product.brand || null,
    productId: product.product_id,
    productKind: product.kind,
    productLineage: product.category || null,
    productName: product.name,
    quantity,
    reservationMode,
    storeType: getStoreType(fulfillmentMode),
  });
};

export const trackProductListingTap = (
  {
    cardLocation,
    fulfillmentMode,
    index,
    product,
    reservationMode,
    storeId,
  }: TrackProductListingTapArgs,
  {
    'ob-gtag-events': gtagEventsEnabled = false,
  }: Partial<Record<FeatureFlag, boolean>> = {}
) => {
  // On pdp, there is no selected weight so we are opting for the first available one
  const selectedWeight = product.available_weights?.[0];
  const price = getPriceIncludingDiscounts(product, selectedWeight ?? 'each');

  const ecommerce = viewItemPayload({
    brand: product.brand || null,
    name: product.name,
    price,
    price_id: selectedWeight || 'each',
    product_id: product.product_id,
    storeId,
  });
  window.dataLayer?.push({
    ecommerce,
    event: 'view_item',
    product: {
      brand: product.brand,
      category: product.category,
      kind: product.kind,
      name: product.name,
      price_id: selectedWeight || 'each',
    },
    productId: product.product_id,
  });
  if (gtagEventsEnabled) {
    window.gtag?.('event', 'view_item', ecommerce);
  }

  return track({
    brandId: Number(product.product_brand_id) || null,
    cardLocation,
    event: EventNames.ProductListingTap,
    hasMultipleWeights: product.available_weights.length > 1,
    index,
    pricePerUnit: price,
    productBrand: product.brand || null,
    productId: product.product_id,
    productKind: product.kind,
    productLineage: product.category || null,
    productName: product.name,
    reservationMode,
    specialTypes: product.specialType,
    storeType: getStoreType(fulfillmentMode),
  });
};

export const trackCustomerRating = (
  {
    fulfillmentMode,
    rating,
    reservationId,
    reservationMode,
  }: TrackCustomerRatingArgs,
  {
    'ob-gtag-events': gtagEventsEnabled = false,
  }: Partial<Record<FeatureFlag, boolean>> = {}
) => {
  window.dataLayer?.push({
    event: 'customer_rating',
    rating,
  });
  if (gtagEventsEnabled) {
    window.gtag?.('event', 'customer_rating', { rating });
  }

  return track({
    event: EventNames.CustomerRating,
    ...buildCustomerRatingProperties({ rating, reservationId }),
    reservationMode,
    storeType: getStoreType(fulfillmentMode),
  });
};

export const trackLoggedIn = (
  { googleSsoEnabled, method, source }: TrackLoggedInArgs,
  {
    'ob-gtag-events': gtagEventsEnabled = false,
  }: Partial<Record<FeatureFlag, boolean>> = {}
) => {
  window.dataLayer?.push({
    event: 'login',
    method,
  });
  if (gtagEventsEnabled) {
    window.gtag?.('event', 'login', { method });
  }

  return track({
    event: EventNames.LoggedIn,
    'Google SSO enabled?': googleSsoEnabled,
    'Sign In Method': method,
    Source: source,
  });
};

export const trackRegistered = (
  { googleSsoEnabled, method, source }: TrackRegisteredArgs,
  {
    'ob-gtag-events': gtagEventsEnabled = false,
  }: Partial<Record<FeatureFlag, boolean>> = {}
) => {
  window.dataLayer?.push({
    event: 'sign_up',
    method,
  });
  if (gtagEventsEnabled) {
    window.gtag?.('event', 'sign_up', { method });
  }

  return track({
    event: EventNames.Registered,
    'Google SSO enabled?': googleSsoEnabled,
    'Sign In Method': method,
    Source: source,
  });
};

export const trackHeaderTap = (
  { reservationMode, tappable }: TrackHeaderTapArgs,
  {
    'ob-gtag-events': gtagEventsEnabled = false,
  }: Partial<Record<FeatureFlag, boolean>> = {}
) => {
  window.dataLayer?.push({
    element: tappable,
    event: 'tap_header',
  });
  if (gtagEventsEnabled) {
    window.gtag?.('event', 'tap_header', { element: tappable });
  }

  return track({
    event: EventNames.HeaderTap,
    'Header Element Selected': tappable,
    reservationMode,
  });
};

export const trackMenuTap = (
  { defaultOption, menuOptionSelected, reservationMode }: TrackMenuTapArgs,
  {
    'ob-gtag-events': gtagEventsEnabled = false,
  }: Partial<Record<FeatureFlag, boolean>> = {}
) => {
  window.dataLayer?.push({
    event: 'tap_menu',
    menu_option_selected: menuOptionSelected,
  });
  if (gtagEventsEnabled) {
    window.gtag?.('event', 'tap_menu', {
      menu_option_selected: menuOptionSelected,
    });
  }

  return track({
    'Default Tab': defaultOption,
    event: EventNames.MenuTap,
    'Menu Option Selected': menuOptionSelected,
    reservationMode,
  });
};

export const trackStoreSet = (
  storeListIndex?: number,
  {
    'ob-gtag-events': gtagEventsEnabled = false,
  }: Partial<Record<FeatureFlag, boolean>> = {}
) => {
  window.dataLayer?.push({
    event: 'set_store',
  });
  if (gtagEventsEnabled) {
    window.gtag?.('event', 'set_store');
  }

  return track({
    event: EventNames.StoreSet,
    storeListIndex,
  });
};

export const trackSearchedMenuProducts = (
  { hits, searchCriteria, theme }: TrackSearchedMenuProductsArgs,
  {
    'ob-gtag-events': gtagEventsEnabled = false,
  }: Partial<Record<FeatureFlag, boolean>> = {}
) => {
  const data = buildSearchedMenuProductsProperties(searchCriteria, hits);
  window.dataLayer?.push({
    event: 'search_menu_products',
    ...data,
  });
  if (gtagEventsEnabled) {
    window.gtag?.('event', 'search_menu_products', data);
  }

  return track({
    event: EventNames.SearchedMenuProducts,
    ...data,
    ...buildCustomComponentProperties(theme),
  });
};

interface TrackViewedProductArgs {
  isOutOfStock: boolean;
  product: ProductSearchResultViewModel | ProductViewModel;
  theme: BloomMenuTheme;
}

export const trackViewedProduct = ({
  isOutOfStock,
  product,
  theme,
}: TrackViewedProductArgs) => {
  return track({
    event: EventNames.ViewedProduct,
    isOutOfStock,
    productBrand: product.brand || null,
    productId: product.product_id,
    productKind: product.kind,
    productName: product.name,
    ...buildCustomComponentProperties(theme),
  });
};

interface TrackStoreSpecialPromptTapArgs {
  special: StoreSpecial;
}

export const trackStoreSpecialPromptTap = ({
  special,
}: TrackStoreSpecialPromptTapArgs) => {
  const discountDetails =
    special.discount_type === 'percent'
      ? special.discount_percent
      : special.discount_type === 'target_price'
        ? special.discount_target_price
        : special.discount_dollar_amount;

  const data = {
    discountDetails: discountDetails ?? 0,
    discountType: special.discount_type,
    specialId: special.id,
    specialName: special.title,
    specialType: special.special_type as SpecialType,
  };

  return track({
    ...data,
    event: EventNames.StoreSpecialPromptTap,
    Source: 'pdp',
  });
};

interface TrackAbTestArgs {
  enabled: boolean;
  experiment: string;
}

export const trackAbTest = ({ enabled, experiment }: TrackAbTestArgs) => {
  return track({
    enabled,
    event: EventNames.AbTest,
    experiment,
  });
};

export const trackWizardOpened = (source: WizardOpenSource) => {
  return track({
    event: EventNames.WizardOpened,
    wizardSource: source,
  });
};

export const trackWizardSelectionConfirmed = (
  wizardStep: WizardStepKey,
  selectedFilters: string[]
) => {
  return track({
    event: EventNames.WizardSelectionConfirmed,
    selectedFilters,
    wizardStep,
  });
};

interface TrackWizardResultsViewedArgs {
  appliedFilters: string[];
  selectedFilters: string[];
  skippedFilters: string[];
  totalProducts: number;
}

export const trackWizardResultsViewed = (
  payload: TrackWizardResultsViewedArgs
) => {
  return track({ event: EventNames.WizardResultsViewed, ...payload });
};

interface TrackPageViewedArgs {
  pageTitle?: string;
  path: string;
  search: string;
}

export const trackPageViewed = ({
  pageTitle,
  path,
  search,
}: TrackPageViewedArgs) =>
  track({
    event: EventNames.PageViewed,
    pageTitle,
    path,
    search,
  });

export const trackStoreSpecialTap = (
  { source, ...props }: TrackStoreSpecialTapArgs,
  {
    'ob-gtag-events': gtagEventsEnabled = false,
  }: Partial<Record<FeatureFlag, boolean>> = {}
) => {
  const data = {
    Source: source,
    ...props,
  };

  window.dataLayer?.push({
    event: 'store_special_tap',
    ...data,
  });
  if (gtagEventsEnabled) {
    window.gtag?.('event', 'store_special_tap', data);
  }

  return track({
    event: EventNames.StoreSpecialTap,
    ...data,
  });
};

interface CompareProductTrackingArgs {
  productBrand?: string | null;
  productId: number;
  productKind?: string;
  productName: string;
}

export const trackClickedCompare = () =>
  track({ event: EventNames.ClickedCompare });

export const trackOpenedCompareModal = (
  productCount: number,
  products: CompareProduct[]
) =>
  track({
    event: EventNames.OpenedCompareModal,
    productCount,
    ...compareProductsTrackingSummary(products),
  });

export const trackClosedCompareModal = (
  productCount: number,
  method: 'cleared_all' | 'dismissed'
) => track({ event: EventNames.ClosedCompareModal, method, productCount });

export const trackAddedProductToCompare = (
  compareCount: number,
  product: CompareProductTrackingArgs
) =>
  track({
    compareCount,
    event: EventNames.AddedProductToCompare,
    ...product,
  });

export const trackClickedCompareModalProduct = (
  product: CompareProductTrackingArgs
) => track({ event: EventNames.ClickedCompareModalProduct, ...product });
